/**
 * Variant Gallery Switcher
 * Handles switching between variant-specific image galleries based on metafield data
 */

(function() {
  'use strict';

  let variantGalleries = null;
  let productMediaElement = null;
  let isInitialized = false;
  let currentVariantId = null;
  let isSwitching = false;

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    // Wait a bit for the theme's ProductMedia to initialize
    setTimeout(initVariantGallerySwitcher, 100);
  }

  function initVariantGallerySwitcher() {
    // Check for variant gallery data first (works for all products with variants)
    const variantGalleryDataElement = document.querySelector('script[data-variant-galleries]');
    
    if (!variantGalleryDataElement) {
      console.log('No variant gallery data found - using default product images');
      return;
    }

    try {
      variantGalleries = JSON.parse(variantGalleryDataElement.textContent);
      console.log('Variant galleries loaded:', variantGalleries);
    } catch (e) {
      console.error('Failed to parse variant gallery data:', e);
      return;
    }

    // Find product media element
    productMediaElement = document.querySelector('product-media');
    
    if (!productMediaElement) {
      console.warn('Product media element not found');
      return;
    }

    const formId = productMediaElement.getAttribute('form-id');
    const productForm = document.getElementById(formId);

    if (!productForm) {
      console.warn('Product form not found:', formId);
      return;
    }

    console.log('Variant gallery switcher initialized');
    isInitialized = true;

    // Listen for variant:changed event which fires AFTER theme's handlers complete
    productForm.addEventListener('variant:changed', handleVariantChange, false);

    // Check if the currently selected variant has a custom gallery and load it
    loadInitialVariantGallery(productForm);
  }

  function loadInitialVariantGallery(productForm) {
    // Get the selected variant ID from the form
    const variantIdInput = productForm.querySelector('input[name="id"]');
    
    if (!variantIdInput || !variantIdInput.value) {
      console.log('No variant selected on page load');
      return;
    }

    const selectedVariantId = variantIdInput.value.toString();
    const galleryData = variantGalleries[selectedVariantId];

    console.log('Initial variant ID:', selectedVariantId, 'Gallery data:', galleryData);

    // Check if gallery is already rendered server-side
    if (productMediaElement.hasAttribute('data-has-variant-gallery') && galleryData && galleryData.length > 0) {
      console.log('Variant gallery already rendered server-side');
      currentVariantId = selectedVariantId;
      return;
    }

    // If this variant has a custom gallery but it's not rendered, load it immediately
    if (galleryData && galleryData.length > 0) {
      console.log('Loading initial variant gallery for variant:', selectedVariantId);
      
      // Create a mock variant object
      const variant = {
        id: parseInt(selectedVariantId),
        product_id: variantIdInput.closest('form').dataset.productId || ''
      };

      // Mark that we have a variant gallery
      productMediaElement.setAttribute('data-has-variant-gallery', 'true');
      currentVariantId = selectedVariantId;

      // Load the gallery with a small delay to ensure DOM is ready
      setTimeout(() => {
        switchGallery(galleryData, variant);
      }, 150);
    } else {
      console.log('No custom gallery for initial variant, showing default product images');
    }
  }

  function handleVariantChange(event) {
    if (!isInitialized || !variantGalleries || !productMediaElement) {
      console.log('Variant gallery not initialized');
      return;
    }

    const variant = event.detail.variant;
    
    if (!variant || !variant.id) {
      console.log('No variant in event');
      return;
    }

    const variantId = variant.id.toString();
    
    // Skip if already showing this variant's gallery
    if (currentVariantId === variantId && productMediaElement.hasAttribute('data-has-variant-gallery')) {
      console.log('Already showing gallery for variant:', variantId);
      return;
    }
    
    // Skip if already switching
    if (isSwitching) {
      console.log('Gallery switch already in progress');
      return;
    }

    const galleryData = variantGalleries[variantId];

    console.log('Variant changed to:', variantId, 'Gallery data:', galleryData);

    // If this variant has no gallery or gallery is empty, let the default system handle it
    if (!galleryData || galleryData.length === 0) {
      console.log('No custom gallery for this variant, using default behavior');
      productMediaElement.removeAttribute('data-has-variant-gallery');
      currentVariantId = null;
      return;
    }
    
    // Mark that we have a variant gallery
    productMediaElement.setAttribute('data-has-variant-gallery', 'true');
    currentVariantId = variantId;

    // Switch gallery (runs after theme's default handler has completed)
    switchGallery(galleryData, variant);
  }

  function switchGallery(galleryData, variant) {
    console.log('Switching gallery for variant:', variant.id);
    isSwitching = true;
    
    const carousel = productMediaElement.querySelector('flickity-carousel');
    const thumbnailList = productMediaElement.querySelector('.product__thumbnail-list-inner');
    const dotsNav = productMediaElement.querySelector('.dots-nav');

    if (!carousel) {
      console.warn('Carousel not found');
      isSwitching = false;
      return;
    }

    console.log('Carousel found, building new gallery');

    // Build new gallery HTML
    const mediaItems = buildMediaItems(galleryData, variant);
    const thumbnails = buildThumbnails(galleryData, variant);
    const dots = buildDots(galleryData);

    // Destroy existing Flickity instance if it exists
    const destroyExisting = async () => {
      if (carousel.flickity) {
        console.log('Waiting for existing Flickity instance');
        const flickityInstance = await carousel.flickity;
        console.log('Destroying existing Flickity instance');
        flickityInstance.destroy();
        carousel.flickity = null;
      }
    };

    destroyExisting().then(() => {
      console.log('Updating gallery HTML');
      
      // Update carousel content
      carousel.innerHTML = mediaItems;

      // Update thumbnails
      if (thumbnailList) {
        thumbnailList.innerHTML = thumbnails;
      }

      // Update dots navigation
      if (dotsNav) {
        dotsNav.innerHTML = dots;
      }

      console.log('Gallery HTML updated, reinitializing carousel');

      // Make sure zoom button is visible for images
      const zoomButton = productMediaElement.querySelector('.product__zoom-button');
      if (zoomButton) {
        zoomButton.removeAttribute('hidden');
      }

      // Reinitialize the carousel
      setTimeout(() => {
        if (carousel._createFlickity && typeof carousel._createFlickity === 'function') {
          console.log('Calling carousel._createFlickity()');
          const flickityPromise = carousel._createFlickity();
          
          if (flickityPromise && flickityPromise.then) {
            flickityPromise.then(() => {
              console.log('Carousel reinitialized successfully');
              setTimeout(() => {
                setupThumbnailClickHandlers();
                isSwitching = false;
                console.log('Gallery switch complete');
              }, 200);
            }).catch((err) => {
              console.error('Error reinitializing carousel:', err);
              isSwitching = false;
            });
          } else {
            console.log('Carousel initialized (no promise returned)');
            setTimeout(() => {
              setupThumbnailClickHandlers();
              isSwitching = false;
              console.log('Gallery switch complete');
            }, 300);
          }
        } else {
          console.warn('_createFlickity not available, using fallback');
          // Fallback: Just setup handlers
          setTimeout(() => {
            setupThumbnailClickHandlers();
            isSwitching = false;
            console.log('Gallery switch complete (fallback)');
          }, 300);
        }
      }, 100);
    }).catch((err) => {
      console.error('Error destroying carousel:', err);
      isSwitching = false;
    });
  }

  function setupThumbnailClickHandlers() {
    const thumbnails = productMediaElement.querySelectorAll('.product__thumbnail-item');
    const carousel = productMediaElement.querySelector('flickity-carousel');
    
    if (!thumbnails.length) {
      console.log('No thumbnails found');
      return;
    }

    console.log('Setting up', thumbnails.length, 'thumbnail click handlers');
    
    thumbnails.forEach((thumb, index) => {
      // Remove any existing listeners by cloning
      const newThumb = thumb.cloneNode(true);
      thumb.parentNode.replaceChild(newThumb, thumb);
      
      newThumb.addEventListener('click', async function(e) {
        e.preventDefault();
        console.log('Thumbnail', index, 'clicked');
        
        if (carousel && carousel.select) {
          try {
            await carousel.select(index);
            console.log('Selected slide', index);
          } catch (err) {
            console.error('Error selecting slide:', err);
          }
        }
        
        // Update aria-current on all thumbnails
        const allThumbs = productMediaElement.querySelectorAll('.product__thumbnail-item');
        allThumbs.forEach(t => t.removeAttribute('aria-current'));
        newThumb.setAttribute('aria-current', 'true');
      });
    });

    console.log('Thumbnail handlers setup complete');
    
    // Also setup dot navigation handlers
    const dots = productMediaElement.querySelectorAll('.dots-nav__item');
    if (dots.length) {
      console.log('Setting up', dots.length, 'dot navigation handlers');
      dots.forEach((dot, index) => {
        const newDot = dot.cloneNode(true);
        dot.parentNode.replaceChild(newDot, dot);
        
        newDot.addEventListener('click', async function(e) {
          e.preventDefault();
          console.log('Dot', index, 'clicked');
          
          if (carousel && carousel.select) {
            try {
              await carousel.select(index);
            } catch (err) {
              console.error('Error selecting slide:', err);
            }
          }
          
          // Update aria-current
          const allDots = productMediaElement.querySelectorAll('.dots-nav__item');
          allDots.forEach(d => d.removeAttribute('aria-current'));
          newDot.setAttribute('aria-current', 'true');
        });
      });
    }
  }

  function buildMediaItems(galleryData, variant) {
    const sectionElement = productMediaElement.closest('[id^="shopify-section-"]');
    const sectionId = sectionElement ? sectionElement.id.replace('shopify-section-', '') : 'main';
    const productId = variant.product_id || '';

    return galleryData.map((media, index) => {
      const aspectRatio = media.preview_image.aspect_ratio || 1;
      const paddingBottom = (100.0 / aspectRatio).toFixed(2);
      const mediaId = media.id || `variant-${variant.id}-${index}`;

      return `
        <div id="product-${sectionId}-${mediaId}" 
             class="product__media-item ${index === 0 ? 'is-initial-selected is-selected' : ''}" 
             data-media-type="image" 
             data-media-id="${mediaId}" 
             data-original-position="${index}">
          <div class="product__media-image-wrapper aspect-ratio aspect-ratio--natural" 
               style="padding-bottom: ${paddingBottom}%; --aspect-ratio: ${aspectRatio}">
            <img src="${media.preview_image.src}" 
                 alt="${media.alt || ''}" 
                 loading="${index === 0 ? 'eager' : 'lazy'}"
                 sizes="(max-width: 999px) calc(100vw - 48px), 640px"
                 width="${media.preview_image.width}"
                 height="${media.preview_image.height}">
          </div>
        </div>
      `;
    }).join('');
  }

  function buildThumbnails(galleryData, variant) {
    const sectionElement = productMediaElement.closest('[id^="shopify-section-"]');
    const sectionId = sectionElement ? sectionElement.id.replace('shopify-section-', '') : 'main';
    const showOnMobile = !productMediaElement.querySelector('.product__thumbnail-item.hidden-pocket');

    return galleryData.map((media, index) => {
      const mediaId = media.id || `variant-${variant.id}-${index}`;
      // Generate thumbnail URL - use Shopify's image sizing
      let thumbnailSrc = media.preview_image.src;
      if (thumbnailSrc.includes('?')) {
        thumbnailSrc = thumbnailSrc.split('?')[0];
      }
      // Add _288x before the file extension
      thumbnailSrc = thumbnailSrc.replace(/(\.[^.]+)$/, '_288x$1');

      return `
        <button type="button" 
                tabindex="-1" 
                class="product__thumbnail-item ${!showOnMobile ? 'hidden-pocket' : ''}" 
                ${index === 0 ? 'aria-current="true"' : ''}
                aria-controls="product-${sectionId}-${mediaId}" 
                data-media-id="${mediaId}" 
                data-action="select">
          <div class="product__thumbnail">
            <img src="${thumbnailSrc}" 
                 alt="${media.alt || ''}" 
                 loading="lazy"
                 sizes="(max-width: 999px) 72px, 60px"
                 width="72"
                 height="${Math.round(72 / (media.preview_image.aspect_ratio || 1))}">
          </div>
        </button>
      `;
    }).join('');
  }

  function buildDots(galleryData) {
    const sectionElement = productMediaElement.closest('[id^="shopify-section-"]');
    const sectionId = sectionElement ? sectionElement.id.replace('shopify-section-', '') : 'main';

    return galleryData.map((media, index) => {
      const mediaId = media.id || `variant-${index}`;
      
      return `
        <button type="button" 
                tabindex="-1" 
                class="dots-nav__item tap-area" 
                ${index === 0 ? 'aria-current="true"' : ''}
                aria-controls="product-${sectionId}-${mediaId}" 
                data-media-id="${mediaId}" 
                data-action="select">
          <span class="visually-hidden">Go to slide ${index + 1}</span>
        </button>
      `;
    }).join('');
  }

})();

